import * as React from 'react';
import { useEffect } from 'react';
import { Controller, Scene } from 'react-scrollmagic';
import JumpNavLink from './JumpNavLink';
import '../css/jump-nav.sass';

interface JumpNavOption {
  name: string;
  sectionId: string;
}

interface JumpNavigationProps {
  options: JumpNavOption[];
  minNumberOfOptions: number;
  theme?: string;
  pageLoaded?: boolean;
  emptyAttributeName?: string;
}
interface JumpNavigationState {
  itemProgressValue: number[];
  isNavBarHovered: boolean;
  options: JumpNavOption[];
}

const MAX_CHECK_CONTENT_ATTEMPTS = 100;
const MS_BETWEEN_CHECKS = 500;

// Repeatedly check for the expected content MAX_CHECK_CONTENT_ATTEMPTS times or until we
// have found it all
async function checkForLoadedElements(
  ids: string[],
  processIdsWithContent: (ids: string[]) => any,
  attemptNumber: number,
  emptyAttributeName: string,
  foundIds: string[]
) {
  const idsWithContent: string[] = foundIds;
  const idsWithNoContent: string[] = ids
    .map(id => {
      const element = document.getElementById(id);
      // If the jump nav is empty but should always be shown, an attribute
      // will be placed on the div with a value of "true"
      if (
        element &&
        !foundIds.includes(id) &&
        (element.getAttribute(emptyAttributeName) === 'true' ||
          element.innerText.trim())
      ) {
        idsWithContent.push(id);
        return '';
      }
      return id;
    })
    .filter(id => id !== '');

  processIdsWithContent(idsWithContent);

  if (attemptNumber < MAX_CHECK_CONTENT_ATTEMPTS && ids.length > 0) {
    setTimeout(
      () =>
        checkForLoadedElements(
          idsWithNoContent,
          processIdsWithContent,
          attemptNumber + 1,
          emptyAttributeName,
          idsWithContent
        ),
      MS_BETWEEN_CHECKS
    );
  }
}

export default function JumpNavigation(props: JumpNavigationProps) {
  const [state, setState] = React.useState<JumpNavigationState>({
    itemProgressValue: [],
    isNavBarHovered: false,
    options: props.options
  });

  const { emptyAttributeName = 'data-empty' } = props;

  const getOptions = () => props.options;

  useEffect(() => {
    if (props.pageLoaded) {
      if (props.options && props.options.length > 0) {
        const processIdsWithContent = (idsWithContent: string[]) => {
          const newOptions = getOptions().filter(opt =>
            idsWithContent.includes(opt.sectionId)
          );
          const uniqueOptions: JumpNavOption[] = [];
          const options = state.options
            .concat(newOptions)
            .filter(option => getOptions().includes(option));
          options.forEach(item => {
            const i = uniqueOptions.findIndex(
              x => x.sectionId === item.sectionId
            );
            if (i === -1) {
              uniqueOptions.push(item);
            }
          });
          setState({
            ...state,
            options: uniqueOptions
          });
        };

        checkForLoadedElements(
          getOptions().map(opt => opt.sectionId),
          processIdsWithContent,
          0,
          emptyAttributeName,
          state.options.map(opt => opt.sectionId)
        );
      } else {
        setState({ ...state, options: [] });
      }
    }
  }, [props.options, props.pageLoaded]);

  // To get the duration of the element to which scene is pointing to
  const getDurationOfElement = (sectionId: string = '') => {
    const element = document.getElementById(sectionId);
    if (element) {
      return element.scrollHeight; // height of the element
    }
    return 0;
  };

  // Updates the state with the current progress based on scroll
  const setItemProgress = (index: number, progress: any) => {
    if (progress >= 0) {
      if (props.options[index]) {
        const element = document.getElementById(props.options[index].sectionId);
        const progressValues = [...state.itemProgressValue];
        // If jump-nav-full class is applied to section, lock progress at 100%
        if (element && element.className.includes('jump-nav-full')) {
          if (progressValues[index] !== 100) {
            progressValues[index] = 100;
            setState({ ...state, itemProgressValue: progressValues });
          }
        } else if (progressValues[index] !== progress) {
          progressValues[index] = progress;
          setState({ ...state, itemProgressValue: progressValues });
        }
      }
    }
  };

  const getPercentageWidth = (index: number) => {
    if (state.isNavBarHovered) {
      return {};
    }
    return { width: `calc(${state.itemProgressValue[index]}%)` };
  };

  const handleMouseEnter = () => {
    setState({ ...state, isNavBarHovered: true });
  };

  const handleMouseLeave = () => {
    setState({ ...state, isNavBarHovered: false });
  };

  return state.options && state.options.length >= props.minNumberOfOptions ? (
    <>
      <div className={`jump-nav ${props.theme ? props.theme : ''}`}>
        {state.options &&
          state.options.map((option, index) => (
            <div className="nav-item" key={option.sectionId}>
              <div
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <JumpNavLink
                  id={`jump-nav-${index}`}
                  label={option.name}
                  sectionId={option.sectionId}
                  key={option.sectionId}
                />
                <div
                  className="progress-bar"
                  style={getPercentageWidth(index)}
                ></div>
              </div>
            </div>
          ))}

        {/* @ts-ignore */}
        <Controller>
          {state.options &&
            state.options.map((item: any, index: number) => {
              return (
                // @ts-ignore
                <Scene
                  key={item.sectionId}
                  duration={getDurationOfElement(item.sectionId)}
                  triggerElement={`#${item.sectionId}`}
                >
                  {(progress: any) => {
                    setItemProgress(index, Math.round(progress * 100));
                    return <></>;
                  }}
                </Scene>
              );
            })}
        </Controller>
      </div>
    </>
  ) : (
    <></>
  );
}
